import type { ActivePlanKey } from '@sparklayer-io/spark-dash-core/constants/features';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Badge, Card, ExternalLink, Heading, Pressable, Text } from '@ui/themed';
import { t } from 'i18next';
import type { PropsWithChildren, ReactNode } from 'react';
import { useHandlers } from '../context/page-context';
import { PlanBadge } from './plan-availability-badge';

type AddonCardProps = PropsWithChildren<{
  title: string;
  /** A picture drawn to the card's width, such as one from `addon-graphics`. */
  illustration: ReactNode;
  learnMoreURL: string;
  /** Footer call-to-action, e.g. a "Speak to our team" or "Upgrade" button. */
  cta?: ReactNode;
  /** Extra classes on the outer wrapper, e.g. to hide the card below a breakpoint. */
  className?: string;
}> &
  ({ price: string; plan?: never } | { plan: ActivePlanKey; price?: never });

export function AddonCard({
  title,
  illustration,
  learnMoreURL,
  cta,
  className,
  children,
  ...badge
}: AddonCardProps) {
  const { navigationHandler } = useHandlers();

  return (
    <div className={cn('flex-1 basis-50 flex-col', className)}>
      <Card hidePadding fullHeight>
        <Pressable
          fullWidth
          ariaLabel={title}
          onClick={() => navigationHandler.navigate(learnMoreURL, { absolute: true, newTab: true })}
        >
          <span className="block bg-spark-surface-subtle *:block *:w-full">{illustration}</span>
        </Pressable>
        <div className="flex grow flex-col gap-2 px-5 py-4">
          <Heading level="h3" style="sm" weight="normal">
            {title}
          </Heading>
          <Text variant="secondary">
            {children}{' '}
            <ExternalLink link={learnMoreURL} showIcon>
              {t('links:learn_more')}
            </ExternalLink>
          </Text>
          {/* `mt-auto` pins the badge and the CTA after it to the bottom of the card */}
          <span className="mt-auto self-start">
            {badge.plan ? <PlanBadge plan={badge.plan} /> : <Badge>{badge.price}</Badge>}
          </span>
          {cta}
        </div>
      </Card>
    </div>
  );
}
